<template>
    <div class="drag-and-drop">
        <template v-if="includePreviews && (layout === 'stacked')">
            <file-card
                    v-for="file in files"
                    :file="file"
                    @remove-file="removeFile"
                    :key="file.name"
            />
        </template>
        <div
                v-cloak
                @drop.prevent="handleDrop"
                @dragover.prevent="setDragState(true)"
                @dragleave.prevent="setDragState(false)"
                :class="componentClasses"
                :style="componentStyles"
        >
            <b-form-file
                    type="file"
                    :ref="componentUid"
                    v-show="false"
                    multiple
                    @input="addFileFromInput($event)"
                    :value="files"
                    :accept="fileTypes"
            />
            <div
                    class="text tw-mt-[2.0625rem]"
                    v-if="!maxFilesReached"
            >
                <template v-if="showDefaultText">
                    <div v-if="showIcon">
                        <i class="cloud-icon far fa-cloud-upload" />
                    </div>
                    <div :class="`title ${titleClasses}`">
                        {{ title }}
                    </div>
                    <div :class="`subtitle ${subtitleClasses}`">
                        <slot name="subtitle">
                            your files here, or
                            <span
                                    :class="`link ${linkClasses}`"
                                    @click="openFileDialog"
                            >
                                browse
                            </span>
                        </slot>
                        <p
                                v-if="description"
                                class="description mb-0"
                        >
                            {{ description }}
                        </p>
                        <p
                                v-else
                                class="max-text"
                        >
                            Maximum {{ bytesFormatter(maxFileSizeKb) }} per file
                        </p>
                    </div>
                </template>
                <template v-if="showAdditionalText">
                    <div
                            class="title title-link"
                            @click="openFileDialog"
                    >
                        <i class="fas fa-plus additional-text-icon" />
                        Add Another
                    </div>
                </template>
            </div>
            <div
                    class="text tw-mt-[3.3125rem]"
                    v-else
            >
                <div class="title">
                    Max Files Uploaded
                </div>
            </div>
        </div>
        <template v-if="includePreviews && (layout !== 'stacked')">
            <file-card
                    v-for="file in files"
                    :file="file"
                    @remove-file="removeFile"
                    :key="file.name"
            />
        </template>
    </div>
</template>

<script>
import FileCard from './FileCard.vue';
import {bytesFormatter} from '../../helpers/stringHelpers';
import UploadFilesMixin from "../../mixins/UploadFilesMixin";
import {TOAST_ICON_TYPES as ICON_TYPES} from "../../mixins/ShowNotificationMixin";

export default {
    name: 'DragAndDrop',
    components: {
        FileCard
    },
    mixins: [
        UploadFilesMixin
    ],
    props: {
        layout: {
            type: String,
            required: false,
            default: 'default',
            validator: (value) => {
                return ['default', 'single', 'stacked'].indexOf(value) !== -1;
            }
        },
        value: {
            type: Array,
            required: true
        },
        multiple: {
            type: Boolean,
            required: false,
            default: true
        },
        includePreviews: {
            type: Boolean,
            required: false,
            default: true
        },
        componentHeight: {
            type: String,
            required: false,
            default: '8.125rem'
        },
        dropFolders: {
            type: Boolean,
            default: false,
        },
        showIcon: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'Drag & Drop',
        },
        description: {
            type: String,
        },
        titleClasses: {
            type: String,
            default: '',
        },
        subtitleClasses: {
            type: String,
            default: '',
        },
        linkClasses: {
            type: String,
            default: '',
        },
        additionalComponentClasses: {
            type: String,
            default: '',
        },
    },
    data: () => {
        return {
            isDragging: false
        };
    },
    computed: {
        files: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        componentStyles() {
            return {
                height: this.calculateHeight,
                display: this.layout === 'single' && this.files.length ? 'none' : 'flex'
            };
        },
        componentClasses() {
            let classes = 'drag-and-drop-zone ';

            if (this.isDragging) {
                classes += 'is-dragging ';
            }

            if (this.additionalComponentClasses) {
                classes += this.additionalComponentClasses;
            }

            return classes;
        },
        showDefaultText() {
            return !(
                (this.layout === 'single' && !!this.files.length) ||
                (this.layout === 'stacked' && this.files.length)
            );
        },
        showAdditionalText() {
            return !!(this.layout === 'stacked' && this.files.length);
        },
        calculateHeight() {
            if (this.layout === 'stacked' && this.files.length) {
                return '3.125rem';
            }
            return this.componentHeight;
        },
        componentUid() {
            return this._uid;
        },
        maxFilesReached() {
            return this.maxFiles <= this.files.length;
        },
        fileTypesArray() {
            return this.fileTypes.split(',').map((item) => item.trim());
        },
        maxFileSizeKb() {
            return this.maxFileSize * Math.pow(10, 6);
        }
    },
    methods: {
        bytesFormatter,
        setDragState(value) {
            this.isDragging = value;
        },
        addFile(e) {
            this.isDragging = false;
            if (this.maxFilesReached) {
                return;
            }
            let droppedFiles = Array.from(e.dataTransfer.files);
            if (!droppedFiles) {
                return;
            }
            if (droppedFiles.length > (this.maxFiles - this.files.length)) {
                droppedFiles.length = this.maxFiles - this.files.length;
            }
            droppedFiles.forEach(fileItem => {
                if ((!this.checkFileExists(fileItem)) && this.checkFileExtension(fileItem) && this.checkFileSize(fileItem)) {
                    this.files.push(fileItem);
                }
                if (!this.checkFileSize(fileItem)) {
                    this.showNotification({
                        message: `File is larger than ${this.bytesFormatter(this.maxFileSizeKb)}`,
                        type: 'error'
                    });
                } else if (!this.checkFileExtension(fileItem)) {
                    this.showNotification({
                        message: `${fileItem.name}: Invalid file format. Please upload one of
                            the following file types: ${this.fileTypes}.`,
                        type: 'error'
                    });
                }
            });
            this.$emit('input', this.files);
        },
        removeFile(file) {
            this.files = this.files.filter(fileItem => fileItem !== file);
            this.$emit('input', this.files);
        },

        async handleDrop(event) {
            this.dropFolders ? await this.getFolderTree(event) : this.addFile(event);
        },

        async getFolderTree(event) {
            let dataTransferItemList = event.dataTransfer.items;
            let entries = [];
            for (let i = 0; i < dataTransferItemList.length; i++) {
                entries.push(dataTransferItemList[i].webkitGetAsEntry());
            }

            if (entries.every(entry => entry.isFile)) {
                this.addFile(event);
                return;
            }

            let folderTree = await this.parseFileSystemEntries(entries);
            this.$emit('input', folderTree);
        },

        async parseFileSystemEntries(entries) {
            let folderTree = [];

            while (entries.length > 0) {
                let entry = entries.shift();
                if (entry.isFile) {
                    if (await this.isFileEntryValid(entry)) {
                        folderTree.push(entry);
                    }
                } else if (entry.isDirectory) {
                    let directoryEntries = await this.readDirectoryEntries(entry.createReader());
                    folderTree.push({
                        directoryName: entry.name,
                        data: [...await this.parseFileSystemEntries(directoryEntries)]
                    });
                }
            }

            return folderTree;
        },

        async isFileEntryValid(fileEntry) {
            if (!this.checkFileExtension(fileEntry)) {
                this.showNotification({
                    message: `${fileEntry.name}: Invalid file format. Please upload one of
                            the following file types: ${this.fileTypes}.`,
                    type: ICON_TYPES.ERROR
                });
                return false;
            }

            return true;
        },

        async readDirectoryEntries(reader) {
            try {
                return await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
            } catch (e) {
                console.error(e);
                return [];
            }
        },

        openFileDialog() {
            this.$refs[this.componentUid].$el.childNodes[0].click();
        },
    }
};
</script>

<style lang="scss">
@import "../../scss/foundation";

.drag-and-drop {
    clear: both;

    .cloud-icon {
        font-size: 3rem;
        color: $black;
    }

    .max-text,
    .description {
        color: $gray-600;
        font-style: italic;
    }

    .drag-and-drop-zone {
        position: relative;
        letter-spacing: 0.0125rem;
        color: #777;
        transition: background-color .2s linear;
        height: 8.125rem;
        text-align: center;
        box-sizing: border-box;
        border: 0.0625rem dashed rgba(173, 177, 187, 0.7);
        border-radius: 0.25rem;
        background-color: $gray-100;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 10px;

        &:hover {
            box-sizing: border-box;
            border: 1px dashed $black;
            border-radius: 4px;
            background-color: $gray-100;
        }

        &.is-dragging {
            box-sizing: border-box;
            border: 1px solid $teal;
            border-radius: 4px;
            background-color: rgba(26, 206, 179, 0.1);
        }

        .text {

            .title {
                color: $black;
                font-family: $headings-font-family;
                font-size: 1rem;
                letter-spacing: 0.01875rem;
                line-height: 1.5rem;
                font-weight: 700;

                .additional-text-icon {
                    font-size: 0.875rem;
                    margin: 5px;
                    color: $black;
                }

                &.title-link {
                    cursor: pointer;
                }
            }

            .subtitle {
                color: $black;
                font-family: $font-family-base;
                font-size: 0.875rem;
                line-height: 1.25rem;

                .link {
                    color: $branding-accent-color;
                    cursor: pointer;

                    &:hover {
                        cursor: pointer;
                        text-decoration: underline;
                    }
                }
            }
        }
    }
}
</style>
